<script setup lang="ts">
import type { NewProductTag } from '@/types/api/api-v4/product'

import CategoryFilter from '@/components/Category/CategoryFilter.vue'
import CategoryFilterDialog from '@/components/Category/CategoryFilterDialog.vue'
import CategoryFilterWidget from '@/components/Category/CategoryFilterWidget.vue'

type Props = {
  filters: NewProductTag[]
  isFiltersActivated: boolean
  selectedFilters: Record<string, number>
  possibleFilters: Record<string, number>
}

withDefaults(defineProps<Props>(), {
  filters: () => [],
})

const emit = defineEmits([
  'resetAllFilters',
  'setActiveFilter',
  'deactivateFilter',
])

const { isMobile } = useResponsive()

const isActiveDialog = ref(false)

const onToggleDialog = (state: boolean) => (isActiveDialog.value = state)

const onResetAllFilters = () => emit('resetAllFilters')

const onSetFilter = (filter: NewProductTag) => emit('setActiveFilter', filter)

const onResetFilter = (filter: NewProductTag) =>
  emit('deactivateFilter', filter)
</script>

<template>
  <template v-if="isMobile">
    <category-filter-widget
      :selected-filters-count="Object.keys(selectedFilters).length"
      @open-filters-dialog="onToggleDialog(true)"
    />

    <category-filter-dialog
      :is-filters-activated="isFiltersActivated"
      :is-filters-dialog-open="isActiveDialog"
      @update:model-value="onToggleDialog"
      @reset-all-filters="onResetAllFilters"
    >
      <div class="filters--wrapper">
        <category-filter
          v-for="(filter, index) in filters"
          :key="index"
          :filter="filter"
          :is-active-filter="filter.slug in selectedFilters"
          :is-possible-filter="filter.slug in possibleFilters"
          :is-filters-activated="isFiltersActivated"
          @set-active-filter="onSetFilter"
          @deactivate-filter="onResetFilter"
        />

        <the-button
          v-if="isFiltersActivated"
          variant="transparent"
          size="xs"
          font="sm"
          radius="lg"
          icon="close-thin"
          indent-left
          @click="onResetAllFilters"
        >
          Сбросить фильтры
        </the-button>
      </div>
    </category-filter-dialog>
  </template>

  <div v-else class="filters--wrapper">
    <category-filter
      v-for="(filter, index) in filters"
      :key="index"
      :filter="filter"
      :is-active-filter="filter.slug in selectedFilters"
      :is-possible-filter="filter.slug in possibleFilters"
      :is-filters-activated="isFiltersActivated"
      @set-active-filter="onSetFilter"
      @deactivate-filter="onResetFilter"
    />

    <transition name="height">
      <the-button
        v-if="isFiltersActivated"
        variant="transparent"
        size="sm"
        font="sm"
        radius="lg"
        icon="close-thin"
        indent-left
        @click="onResetAllFilters"
      >
        Сбросить фильтры
      </the-button>
    </transition>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/styles/animations.scss' as *;

.filters--wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 10px;
  padding: 0 0 30px;
}
</style>
